// Typed client for the Atlas control-plane API. Auth is the BFF httpOnly session
// cookie (no token in the browser): calls are same-origin (dev-proxied to the
// API) with credentials, and the API reads the session. A 401 means the session
// expired → bounce to the BFF login.

const BASE = (import.meta.env.ATLAS_API_URL as string) ?? '';

export interface TenantView {
  tenantId: string;
  role: string;
  name: string;
  slug: string;
  hosts?: string[]; // tenant's public domain hosts (env-specific) for "View live"
}

export interface TenantConfigView {
  marketingUrl: string | null;
  docsUrl: string | null;
  docsEnabled: boolean;
  leadRecipients: string[];
  leadsScoped: boolean;
  leadSubject: string | null;
  salesEmail: string | null;
  calendlyUrl: string | null;
}
export type PageKind = 'PAGE' | 'TEMPLATE';

export interface PageSummary {
  id: string;
  slug: string;
  kind: PageKind;
  status: string;
  reviewStatus: string;
  seoTitle: string | null;
  updatedAt: string;
  hasUnpublished: boolean;
}

export interface EntrySummary {
  id: string;
  collection: string;
  slug: string;
  title: string;
  status: string;
  publishedAt: string | null;
  updatedAt: string;
}
export interface EntryForEdit {
  id: string;
  collection: string;
  slug: string;
  title: string;
  excerpt: string | null;
  bodyMarkdown: string | null;
  featureImageUrl: string | null;
  minuteRead: string | null;
  externalLink: string | null;
  publishedAt: string | null;
  status: string;
}
export interface EntryInput {
  slug: string;
  title: string;
  excerpt?: string | null;
  bodyMarkdown?: string | null;
  featureImageUrl?: string | null;
  minuteRead?: string | null;
  externalLink?: string | null;
  publishedAt?: string | null;
  status?: string;
}

// The content types shown in the left nav. Pages/Templates are block-based
// (Page records, split by kind); the rest are Entry collections.
export type NavKey = 'pages' | 'templates' | 'insight' | 'new' | 'solution-brief';
export interface ContentType {
  key: NavKey;
  label: string;
  kind: 'page' | 'entry';
  pageKind?: PageKind; // for kind === 'page'
  collection?: string; // for kind === 'entry'
  singular: string;
}
export const CONTENT_TYPES: ContentType[] = [
  { key: 'pages', label: 'Pages', kind: 'page', pageKind: 'PAGE', singular: 'page' },
  { key: 'templates', label: 'Templates', kind: 'page', pageKind: 'TEMPLATE', singular: 'template' },
  { key: 'insight', label: 'Insights', kind: 'entry', collection: 'insight', singular: 'insight' },
  { key: 'new', label: 'News', kind: 'entry', collection: 'new', singular: 'news article' },
  { key: 'solution-brief', label: 'Solution Briefs', kind: 'entry', collection: 'solution-brief', singular: 'solution brief' },
];
export interface ReviewItem {
  id: string;
  slug: string;
  revisionId: string;
  submittedBy: string | null;
  submittedAt: string;
}
export interface Block {
  type: string;
  props: Record<string, unknown>;
}
export interface PageForEdit {
  id: string;
  slug: string;
  status: string;
  revisionStatus: string;
  revisionId: string | null;
  publishedRevisionId: string | null;
  blocks: Block[];
  seo: Record<string, unknown> | null;
}
export interface MemberView {
  userId: string;
  email: string;
  displayName: string | null;
  role: string;
  status: string;
}

export type LeadStatus = 'NEW' | 'REVIEWED' | 'SENT' | 'SPAM';
export interface LeadView {
  id: string;
  status: LeadStatus;
  originTenantId: string | null;
  sourceSite: string | null;
  sourcePage: string | null;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  title: string | null;
  company: string | null;
  email: string;
  phone: string | null;
  message: string | null;
  leadSource: string | null;
  sentTo: string[];
  sentAt: string | null;
  createdAt: string;
}

export interface MediaAsset {
  id: string;
  url: string;
  filename: string;
  mime: string;
  size: number;
  width: number | null;
  height: number | null;
  alt: string | null;
  createdAt: string;
}
export interface MediaPage {
  items: MediaAsset[];
  total: number;
  page: number;
  pageSize: number;
}

export interface DocSpaceAdmin { id: string; slug: string; name: string; version: string; status: string; isDefault: boolean; logoUrl?: string | null; pageCount?: number }
export interface ApiTokenView { id: string; name: string; prefix: string; scopes: string[]; createdAt: string; lastUsedAt: string | null; expiresAt: string | null; revokedAt: string | null }
export interface DocReviewItem { pageId: string; revisionId: string; path: string; title: string; spaceId: string; version: string; source: string; changeNote: string | null; submittedBy: string | null; submittedAt: string; openComments: number }
export interface DocComment { id: string; pageId: string; revisionId: string | null; authorUserId: string | null; authorLabel: string | null; body: string; resolved: boolean; parentId: string | null; createdAt: string }
export interface DocTreeAdminNode {
  id: string; title: string; path: string; slug: string; order: number;
  status: string; published: boolean; draftAhead: boolean; protected: boolean; children: DocTreeAdminNode[];
}
export interface DocPageAdmin {
  id: string; spaceId: string; parentId: string | null; title: string; slug: string; path: string;
  status: string; publishedRevisionId: string | null; revisionId: string | null; content: any;
  reviewStatus: string; source: string; changeNote: string | null;
  access: string; requiredEntitlements: string[];
}

// Assignable in the Team dropdown. WEB_EDITOR = website-only, DOCS_EDITOR = docs-only
// (the two scoped roles); the generic roles above them cover both surfaces.
export const ROLES = ['PRODUCT_ADMIN', 'EDITOR', 'REVIEWER', 'AUTHOR', 'CONTRIBUTOR', 'WEB_EDITOR', 'DOCS_EDITOR', 'BUSINESS_MEMBER'] as const;
export const ROLE_LABEL: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin', PRODUCT_ADMIN: 'Product Admin', EDITOR: 'Editor',
  REVIEWER: 'Reviewer', AUTHOR: 'Author', CONTRIBUTOR: 'Contributor',
  WEB_EDITOR: 'Website Editor', DOCS_EDITOR: 'Docs Editor', BUSINESS_MEMBER: 'Business Member',
};
export const ADMIN_ROLES = ['SUPER_ADMIN', 'PRODUCT_ADMIN'];
// Leads Dashboard gate — MUST mirror LEADS_ROLES in apps/api/src/index.ts.
export const LEADS_ROLES = ['SUPER_ADMIN', 'PRODUCT_ADMIN', 'BUSINESS_MEMBER'];
export const REVIEW_ROLES = ['SUPER_ADMIN', 'PRODUCT_ADMIN', 'EDITOR', 'REVIEWER'];
// These MUST mirror the backend sets in apps/api/src/index.ts. The website and docs
// surfaces are gated independently — do NOT derive one from the other, or a
// surface-scoped role (WEB_EDITOR / DOCS_EDITOR) leaks into the wrong surface.
export const EDIT_ROLES = ['SUPER_ADMIN', 'PRODUCT_ADMIN', 'EDITOR', 'REVIEWER', 'AUTHOR', 'CONTRIBUTOR', 'WEB_EDITOR'];
export const PUBLISH_ROLES = ['SUPER_ADMIN', 'PRODUCT_ADMIN', 'EDITOR', 'WEB_EDITOR'];
export const DOC_EDIT_ROLES = ['SUPER_ADMIN', 'PRODUCT_ADMIN', 'EDITOR', 'REVIEWER', 'AUTHOR', 'CONTRIBUTOR', 'DOCS_EDITOR'];
export const DOC_PUBLISH_ROLES = ['SUPER_ADMIN', 'PRODUCT_ADMIN', 'EDITOR', 'DOCS_EDITOR'];

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

export interface SessionUser {
  sub: string;
  email: string;
}

export function loginRedirect(): void {
  window.location.href = `${BASE}/auth/login?returnTo=${encodeURIComponent(location.pathname)}`;
}
export function logoutRedirect(): void {
  window.location.href = `${BASE}/auth/logout`;
}

// Site chrome (header/footer) shape. Kept structurally in sync with
// @atlas/ui's SiteChrome — the API just stores/returns this JSON verbatim, so we
// mirror the type here rather than depend on @atlas/ui in the admin bundle.
export interface ChromeNavChild { label: string; href: string; description?: string; external?: boolean }
export interface ChromeNavColumn { label?: string; href?: string; links: ChromeNavChild[] }
export interface ChromeNavItem {
  label: string;
  href?: string;
  external?: boolean;
  isNew?: boolean;
  description?: string;
  columns?: ChromeNavColumn[]; // mega-menu
  children?: ChromeNavChild[]; // simple dropdown
}
export interface ChromeCta { label: string; href: string; variant?: 'primary' | 'ghost'; external?: boolean }
export interface ChromeFooterColumn { title: string; links: { label: string; href: string }[] }
export interface ChromeSocial { label: string; href: string; iconUrl?: string }
export interface ChromeFooterContact {
  title?: string; emailText?: string; email?: string; emailHref?: string;
  phoneText?: string; phone?: string; phoneHref?: string;
}
export interface ChromeAnnouncement { text: string; linkText?: string; linkHref?: string; external?: boolean }
// Docs site header/footer chrome (simpler than the marketing SiteChrome).
export interface DocsChrome {
  headerLinks?: { label: string; href: string; external?: boolean }[];
  footerColumns?: { title: string; links: { label: string; href: string }[] }[];
  footerNote?: string;
  socials?: { label: string; href: string }[];
}

export interface SiteChrome {
  logoText: string;
  logoUrl?: string;
  footerLogoUrl?: string;
  announcement?: ChromeAnnouncement;
  nav: ChromeNavItem[];
  headerCtas?: ChromeCta[];
  footerColumns: ChromeFooterColumn[];
  footerContact?: ChromeFooterContact;
  socials?: ChromeSocial[];
  legalLinks?: { label: string; href: string }[];
  copyright?: string;
}

export function makeApi() {
  async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await fetch(BASE + path, {
      method,
      credentials: 'include',
      headers: body !== undefined ? { 'content-type': 'application/json' } : {},
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (res.status === 401) {
      loginRedirect();
      throw new ApiError(401, 'unauthenticated');
    }
    if (res.status === 204) return null as T;
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new ApiError(res.status, (data as any).error || `HTTP ${res.status}`, (data as any).details);
    return data as T;
  }
  return {
    // Session probe on load — returns null (not a redirect) when logged out so
    // we can show the branded login card.
    me: async (): Promise<SessionUser | null> => {
      const res = await fetch(BASE + '/auth/me', { credentials: 'include' });
      if (res.status === 401) return null;
      if (!res.ok) throw new ApiError(res.status, 'me_failed');
      return res.json();
    },
    myTenants: () => req<TenantView[]>('GET', '/me/tenants'),
    pages: (t: string, kind?: PageKind) =>
      req<PageSummary[]>('GET', `/tenants/${t}/pages${kind ? `?kind=${kind}` : ''}`),
    page: (t: string, id: string) => req<PageForEdit>('GET', `/tenants/${t}/pages/${id}`),
    blockSamples: (t: string) => req<Record<string, Record<string, unknown>>>('GET', `/tenants/${t}/block-samples`),
    createPage: (t: string, slug: string) => req<{ id: string }>('POST', `/tenants/${t}/pages`, { slug }),
    duplicatePage: (t: string, id: string) => req<{ id: string }>('POST', `/tenants/${t}/pages/${id}/duplicate`),
    deletePage: (t: string, id: string) => req<{ ok: true }>('DELETE', `/tenants/${t}/pages/${id}`),
    saveDraft: (t: string, id: string, blocks: Block[], seo: Record<string, unknown> | null) =>
      req<{ revisionId: string }>('PUT', `/tenants/${t}/pages/${id}/revision`, { blocks, seo }),
    publish: (t: string, id: string, revisionId?: string) =>
      req<{ ok: true }>('POST', `/tenants/${t}/pages/${id}/publish`, { revisionId }),
    submit: (t: string, id: string) => req<{ ok: true }>('POST', `/tenants/${t}/pages/${id}/submit`),
    requestChanges: (t: string, id: string) => req<{ ok: true }>('POST', `/tenants/${t}/pages/${id}/request-changes`),
    reviewQueue: (t: string) => req<ReviewItem[]>('GET', `/tenants/${t}/review-queue`),
    entries: (t: string, collection: string) =>
      req<EntrySummary[]>('GET', `/tenants/${t}/entries?collection=${encodeURIComponent(collection)}`),
    entry: (t: string, id: string) => req<EntryForEdit>('GET', `/tenants/${t}/entries/${id}`),
    createEntry: (t: string, collection: string, data: EntryInput) =>
      req<{ id: string }>('POST', `/tenants/${t}/entries`, { collection, ...data }),
    updateEntry: (t: string, id: string, data: EntryInput) =>
      req<{ ok: true }>('PUT', `/tenants/${t}/entries/${id}`, data),
    deleteEntry: (t: string, id: string) =>
      req<{ ok: true }>('DELETE', `/tenants/${t}/entries/${id}`),
    publishEntry: (t: string, id: string) =>
      req<{ ok: true }>('POST', `/tenants/${t}/entries/${id}/publish`),
    unpublishEntry: (t: string, id: string) =>
      req<{ ok: true }>('POST', `/tenants/${t}/entries/${id}/unpublish`),
    media: (t: string, q = '', page = 1, pageSize = 24) => {
      const p = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (q) p.set('q', q);
      return req<MediaPage>('GET', `/tenants/${t}/media?${p}`);
    },
    uploadMedia: async (t: string, file: File): Promise<MediaAsset> => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BASE}/tenants/${t}/media`, { method: 'POST', credentials: 'include', body: fd });
      if (res.status === 401) { loginRedirect(); throw new ApiError(401, 'unauthenticated'); }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new ApiError(res.status, (data as any).error || `HTTP ${res.status}`);
      return data as MediaAsset;
    },
    deleteMedia: (t: string, id: string) => req<{ ok: true }>('DELETE', `/tenants/${t}/media/${id}`),
    // Docs authoring
    docSpaces: (t: string) => req<DocSpaceAdmin[]>('GET', `/tenants/${t}/doc-spaces`),
    docVersions: (t: string, slug = 'help') => req<DocSpaceAdmin[]>('GET', `/tenants/${t}/doc-versions?slug=${encodeURIComponent(slug)}`),
    createDocVersion: (t: string, body: { slug?: string; fromVersion: string; newVersion: string; name?: string }) =>
      req<{ id: string }>('POST', `/tenants/${t}/doc-versions`, body),
    setDefaultVersion: (t: string, spaceId: string) => req<{ ok: true }>('POST', `/tenants/${t}/doc-spaces/${spaceId}/default`),
    setVersionStatus: (t: string, spaceId: string, status: string) => req<{ ok: true }>('POST', `/tenants/${t}/doc-spaces/${spaceId}/status`, { status }),
    setDocSpaceLogo: (t: string, spaceId: string, url: string | null) => req<{ ok: true }>('POST', `/tenants/${t}/doc-spaces/${spaceId}/logo`, { url }),
    setDocAccess: (t: string, id: string, access: string, entitlements: string[]) =>
      req<{ ok: true }>('POST', `/tenants/${t}/doc-pages/${id}/access`, { access, entitlements }),
    docTree: (t: string, spaceId: string) => req<DocTreeAdminNode[]>('GET', `/tenants/${t}/doc-spaces/${spaceId}/tree`),
    docPage: (t: string, id: string) => req<DocPageAdmin>('GET', `/tenants/${t}/doc-pages/${id}`),
    createDocPage: (t: string, spaceId: string, body: { title: string; parentId?: string | null; slug?: string }) =>
      req<{ id: string }>('POST', `/tenants/${t}/doc-spaces/${spaceId}/pages`, body),
    saveDocRevision: (t: string, id: string, content: unknown, title?: string) =>
      req<{ revisionId: string }>('PUT', `/tenants/${t}/doc-pages/${id}/revision`, { content, title }),
    publishDocPage: (t: string, id: string) => req<{ ok: true }>('POST', `/tenants/${t}/doc-pages/${id}/publish`),
    moveDocPage: (t: string, id: string, dir: 'up' | 'down') => req<{ ok: true }>('POST', `/tenants/${t}/doc-pages/${id}/move`, { dir }),
    renameDocPage: (t: string, id: string, title: string) => req<{ ok: true }>('PATCH', `/tenants/${t}/doc-pages/${id}`, { title }),
    deleteDocPage: (t: string, id: string) => req<{ ok: true }>('DELETE', `/tenants/${t}/doc-pages/${id}`),
    // Docs review workflow + comments
    submitDocReview: (t: string, id: string) => req<{ ok: true }>('POST', `/tenants/${t}/doc-pages/${id}/submit-review`),
    requestDocChanges: (t: string, id: string, body: string) => req<{ ok: true }>('POST', `/tenants/${t}/doc-pages/${id}/request-changes`, { body }),
    docReviewQueue: (t: string) => req<DocReviewItem[]>('GET', `/tenants/${t}/doc-reviews`),
    docComments: (t: string, id: string) => req<DocComment[]>('GET', `/tenants/${t}/doc-pages/${id}/comments`),
    addDocComment: (t: string, id: string, body: string) => req<DocComment>('POST', `/tenants/${t}/doc-pages/${id}/comments`, { body }),
    resolveDocComment: (t: string, id: string, resolved: boolean) => req<{ ok: true }>('POST', `/tenants/${t}/doc-comments/${id}/resolve`, { resolved }),
    docPageDiff: (t: string, id: string) =>
      req<{ published: string; draft: string }>('GET', `/tenants/${t}/doc-pages/${id}/diff`),
    // API tokens (admin)
    apiTokens: (t: string) => req<ApiTokenView[]>('GET', `/tenants/${t}/api-tokens`),
    createApiToken: (t: string, body: { name: string; scopes?: string[]; expiresAt?: string | null }) =>
      req<{ token: string; record: ApiTokenView }>('POST', `/tenants/${t}/api-tokens`, body),
    revokeApiToken: (t: string, id: string) => req<{ ok: true }>('DELETE', `/tenants/${t}/api-tokens/${id}`),
    importDocx: async (t: string, spaceId: string, file: File, parentId?: string | null): Promise<{ id: string; title: string }> => {
      const fd = new FormData();
      fd.append('file', file);
      const qs = parentId ? `?parentId=${parentId}` : '';
      const res = await fetch(`${BASE}/tenants/${t}/doc-spaces/${spaceId}/import-docx${qs}`, { method: 'POST', credentials: 'include', body: fd });
      if (res.status === 401) { loginRedirect(); throw new ApiError(401, 'unauthenticated'); }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new ApiError(res.status, (data as any).error || `HTTP ${res.status}`);
      return data as { id: string; title: string };
    },
    // Site chrome (shared header/footer). null chrome → web app uses defaults.
    chrome: (t: string) => req<{ siteId: string; theme: string; chrome: SiteChrome | null } | null>('GET', `/tenants/${t}/chrome`),
    saveChrome: (t: string, chrome: SiteChrome) => req<{ ok: true }>('PUT', `/tenants/${t}/chrome`, chrome),
    // Docs site header/footer chrome (per docs space, default slug 'help').
    docsChrome: (t: string, slug = 'help') => req<{ chrome: DocsChrome | null }>('GET', `/tenants/${t}/docs-chrome?slug=${encodeURIComponent(slug)}`),
    saveDocsChrome: (t: string, chrome: DocsChrome, slug = 'help') => req<{ ok: true }>('PUT', `/tenants/${t}/docs-chrome`, { chrome, slug }),
    members: (t: string) => req<MemberView[]>('GET', `/tenants/${t}/members`),
    invite: (t: string, email: string, role: string) =>
      req<{ ok: true; emailed?: boolean; reason?: string }>('POST', `/tenants/${t}/members`, { email, role }),
    setRole: (t: string, userId: string, role: string) =>
      req<{ ok: true }>('PUT', `/tenants/${t}/members/${userId}`, { role }),
    removeMember: (t: string, userId: string) =>
      req<{ ok: true }>('DELETE', `/tenants/${t}/members/${userId}`),
    // Leads Dashboard (global inbox).
    leadRecipients: (t: string) => req<{ recipients: string[] }>('GET', `/tenants/${t}/lead-recipients`),
    leads: (t: string, status?: LeadStatus) =>
      req<LeadView[]>('GET', `/tenants/${t}/leads${status ? `?status=${status}` : ''}`),
    setLeadStatus: (t: string, id: string, status: LeadStatus) =>
      req<LeadView>('PUT', `/tenants/${t}/leads/${id}/status`, { status }),
    sendLead: (t: string, id: string, recipients: string[]) =>
      req<LeadView & { emailed: boolean; dryRun: boolean }>('POST', `/tenants/${t}/leads/${id}/send`, { recipients }),
    deleteLead: (t: string, id: string) => req<{ ok: true }>('DELETE', `/tenants/${t}/leads/${id}`),
    // Tenant Setup (per-tenant config). Read: any member. Write: Super Admin.
    tenantConfig: (t: string) => req<TenantConfigView>('GET', `/tenants/${t}/config`),
    saveTenantConfig: (t: string, cfg: Partial<TenantConfigView>) =>
      req<TenantConfigView>('PUT', `/tenants/${t}/config`, cfg),
  };
}

export type Api = ReturnType<typeof makeApi>;
